import { format } from 'date-fns';
import { inventoryStatus } from '@onward-delivery/core';
import { DEFAULT_STOCK_MAX, DEFAULT_STOCK_MIN, DWELL_WARNING_DAYS, INVENTORY_STATUS_DISPLAY } from './constants';

const DASH = '-';

export const itemStatusDisplay = (row) => INVENTORY_STATUS_DISPLAY[inventoryStatus(row)] || null;

export const itemStatusLabel = (row) => itemStatusDisplay(row)?.label || DASH;

export const shipperLabel = ({ shipper_count, shipper_name } = {}) => {
    if (shipper_count > 1) {
        return 'Multiple';
    }
    return shipper_name || DASH;
};

// Compact by design: these sit in narrow table columns that get scanned down,
// not read as prose.
export const formatDwell = (days) => {
    if (days === null || days === undefined) {
        return DASH;
    }
    return `${Math.round(days)}d`;
};

export const formatDate = (value) => (value ? format(new Date(value), 'MMM d, yyyy') : DASH);

export const palletLabel = (pallet) => pallet?.pallet_name || pallet?.pallet_number || DASH;

const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

export const stockLevelPercent = ({ on_hand_count, inventory_min, inventory_max } = {}) => {
    const min = inventory_min ?? DEFAULT_STOCK_MIN;
    const max = inventory_max ?? DEFAULT_STOCK_MAX;
    // An empty or inverted range has no meaningful fill, and dividing by it
    // would put NaN or Infinity into a width style.
    if (max <= min) {
        return null;
    }
    return clamp((((on_hand_count ?? 0) - min) / (max - min)) * 100, 0, 100);
};

/**
 * True for a SKU whose only units are returns. On Hand and Stock Level count delivery stock, so they don't
 * apply to it.
 */
export const isReturnOnly = ({ total_count, return_count } = {}) => !Number(total_count) && Number(return_count) > 0;

export const isBelowMin = (row = {}) =>
    row.inventory_min !== null &&
    row.inventory_min !== undefined &&
    !isReturnOnly(row) &&
    (row.on_hand_count ?? 0) < row.inventory_min;

export const isOverDwell = (days) => (days ?? 0) > DWELL_WARNING_DAYS;

export const lowStockCount = (rows) => (rows || []).filter(isBelowMin).length;

// Only the text a shipper or carrier can actually read off the row. Numeric
// columns are deliberately excluded: matching them meant typing a quantity
// returned SKUs whose on-hand happened to contain those digits.
export const matchesSkuText = (row, needle, { includeShipper = false } = {}) => {
    const fields = [row?.sku, row?.description];
    if (includeShipper) {
        fields.push(shipperLabel(row));
    }
    return fields.some((field) => (field || '').toLowerCase().includes(needle));
};

// `matchedSkus` is what the server said the term hits on serial number or line
// id. A row can qualify on its own text, on one of its items, or both.
//
// The distinction matters downstream: `itemMatchedSkus` holds only the rows that
// would NOT be here on their own text, and those are the ones whose child table
// should narrow to the matching items. A row that matched on description while
// an item search is running keeps all of its items — filtering it to the item
// matches would empty a table the user reached by another route.
export const filterSkuRows = (rows, term, matchedSkus, { includeShipper = false } = {}) => {
    const needle = (term || '').trim().toLowerCase();
    const all = rows || [];
    if (!needle) {
        return { rows: all, itemMatchedSkus: new Set() };
    }

    const itemMatchedSkus = new Set();
    const visible = all.filter((row) => {
        const textMatch = matchesSkuText(row, needle, { includeShipper });
        const itemMatch = Boolean(matchedSkus && matchedSkus.has(row?.sku));
        if (itemMatch && !textMatch) {
            itemMatchedSkus.add(row.sku);
        }
        return textMatch || itemMatch;
    });

    return { rows: visible, itemMatchedSkus };
};

export const resolveItemSearchMatches = ({ term, debounced, matches, minLength }) => {
    const live = (term || '').trim();
    const trimmed = (debounced || '').trim();
    const enabled = trimmed.length >= minLength;
    const stale = live !== trimmed;
    const rows = enabled && !stale ? matches || [] : [];
    return {
        matchedSkus: new Set(rows.map((match) => match.sku)),
        matchedItemIds: new Set(rows.map((match) => match.item_id)),
        stale,
        enabled,
    };
};

export const formatStatValue = (value, { loading, error } = {}) => {
    if (error || (loading && value == null)) {
        return '—';
    }
    return (value ?? 0).toLocaleString();
};

// A party with no business name still gets an entry: dropping it would make its
// inventory unreachable through the filter.
export const facetOptions = (rows, facet) => {
    if (!facet) {
        return [];
    }
    const byId = new Map();
    (rows || []).forEach((row) => {
        const value = row?.[facet.idField];
        if (!value || byId.has(value)) {
            return;
        }
        byId.set(value, { value, label: row[facet.nameField] || 'Unnamed' });
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const STATUS_COUNT_KEYS = {
    INBOUND: 'inbound_count',
    ON_HAND: 'on_hand_count',
    OUTBOUND: 'outbound_count',
};

const RETURN_STAGE_COUNT_KEYS = {
    CREATED: 'return_created_count',
    IN_TRANSIT: 'return_in_transit_count',
    PENDING_PUT_AWAY: 'return_pending_put_away_count',
    STORED: 'return_stored_count',
    TRAILER_SCHEDULED: 'return_trailer_scheduled_count',
    PICKED: 'return_picked_count',
    STAGED: 'return_staged_count',
    LOADED: 'return_loaded_count',
};

export const rollupFacetItems = (items) => {
    const bySku = new Map();
    const shippersBySku = new Map();
    const summary = {
        inbound_count: 0,
        on_hand_count: 0,
        outbound_count: 0,
        return_count: 0,
        ...Object.fromEntries(Object.values(RETURN_STAGE_COUNT_KEYS).map((key) => [key, 0])),
    };

    (items || []).forEach(({ sku, status, return_stage, dwell_days, shipper_id, shipper_name }) => {
        if (!bySku.has(sku)) {
            shippersBySku.set(sku, new Map());
            bySku.set(sku, {
                inbound_count: 0,
                on_hand_count: 0,
                outbound_count: 0,
                total_count: 0,
                return_count: 0,
                max_dwell_days: null,
            });
        }
        const counts = bySku.get(sku);

        const statusKey = STATUS_COUNT_KEYS[status];
        if (statusKey) {
            counts[statusKey] += 1;
            summary[statusKey] += 1;
        }
        if (status !== null && status !== undefined) {
            counts.total_count += 1;
            shippersBySku.get(sku).set(shipper_id, shipper_name);
            if (dwell_days !== null && dwell_days !== undefined) {
                counts.max_dwell_days = Math.max(counts.max_dwell_days ?? dwell_days, dwell_days);
            }
        }
        if (return_stage !== null && return_stage !== undefined) {
            counts.return_count += 1;
            summary.return_count += 1;
            const stageKey = RETURN_STAGE_COUNT_KEYS[return_stage];
            if (stageKey) {
                summary[stageKey] += 1;
            }
        }
    });

    bySku.forEach((counts, sku) => {
        const shippers = shippersBySku.get(sku);
        counts.shipper_count = shippers.size;
        counts.shipper_name = shippers.size === 1 ? shippers.values().next().value : null;
    });

    return { bySku, summary };
};

// `bySku` is null when nothing is selected, which means "do not narrow" —
// distinct from an empty map, which means the selection matched nothing.
export const applyFacetRollup = (rows, bySku) => {
    if (!bySku) {
        return rows || [];
    }
    return (rows || []).filter((row) => bySku.has(row?.sku)).map((row) => ({ ...row, ...bySku.get(row.sku) }));
};

// Applied locally — the child rows are already loaded. Without it, filtering to one
// carrier would show a matching SKU whose expanded list holds another carrier's items.
export const filterItemsByFacet = (items, facet, selectedIds) => {
    if (!facet || !selectedIds?.length) {
        return items || [];
    }
    const wanted = new Set(selectedIds);
    return (items || []).filter((item) => wanted.has(item?.[facet.idField]));
};
